// DashboardV3 — F9.2 WAVE 2C premium-dark rewrite.
//
// Layout:
//   Header  (HeaderV3 if flag ON, else legacy Header)
//   Main
//     - WelcomeBannerV3 (mode auto-detected from /onboarding/status)
//     - StatsRowV3      (real credits + mock leads/campaigns/reputation)
//     - QuickActionsRowV3
//     - AIAgentsPreviewV3 (MOCKED)
//     - RecentActivityV3  (MOCKED)
//
// The legacy Sidebar + AIPanel are NOT rendered here — DashboardV3 uses
// HeaderV3's mini-dashboard widget as the sole navigation surface.
import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import confetti from "canvas-confetti";

import HeaderV2 from "@/pages/landing-v2/components/HeaderV2";
import HeaderV3 from "@/components/header-v3/HeaderV3";
import useHeaderV3Enabled from "@/hooks/useHeaderV3Enabled";
import { useAuth } from "@/context/AuthContext";
import { apiFetch } from "@/lib/api";

import WelcomeBannerV3 from "./sections/WelcomeBannerV3";
import StatsRowV3 from "./sections/StatsRowV3";
import QuickActionsRowV3 from "./sections/QuickActionsRowV3";
import AIAgentsPreviewV3 from "./sections/AIAgentsPreviewV3";
import RecentActivityV3 from "./sections/RecentActivityV3";
import TopUpModalV3 from "./components/TopUpModalV3";
import DashboardPlanWidget from "@/components/dashboard/DashboardPlanWidget";

// Demo values — see spec "3c". Real endpoints wire-up is a P1 follow-up.
const MOCK_LEADS_COUNT = 12;
const MOCK_CAMPAIGNS_COUNT = 3;
const MOCK_REPUTATION_SCORE = 870;

function firstNameOf(user) {
    if (!user) return "";
    const raw = (user.name || user.email || "").trim();
    if (!raw) return "";
    return raw.split(/[\s@]/)[0];
}

export default function DashboardV3() {
    const { t } = useTranslation("dashboard_v3");
    const { user, refresh: refreshAuth } = useAuth();
    const { enabled: headerV3Enabled } = useHeaderV3Enabled();

    const [statusLoading, setStatusLoading] = useState(true);
    const [onboarding, setOnboarding] = useState({
        steps: { email: false, target: false, campaign: false },
        skipped: false,
        completed: false,
        bonus_granted: false,
    });
    const [skipping, setSkipping] = useState(false);
    const [topUpOpen, setTopUpOpen] = useState(false);

    // Pin the body bg so hot-reloads never flash the previous background.
    useEffect(() => {
        const prev = document.body.style.backgroundColor;
        document.body.style.backgroundColor = "#0A0A0A";
        return () => { document.body.style.backgroundColor = prev; };
    }, []);

    const loadStatus = useCallback(async () => {
        try {
            const data = await apiFetch("/dashboard-v3/onboarding/status");
            setOnboarding({
                steps: data?.steps || { email: false, target: false, campaign: false },
                skipped: !!data?.skipped,
                completed: !!data?.completed,
                bonus_granted: !!data?.bonus_granted,
            });
        } catch {
            // Silent fail — banner falls back to "returning" via defaults
            setOnboarding({ steps: {}, skipped: false, completed: true, bonus_granted: true });
        } finally {
            setStatusLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user) loadStatus();
    }, [user, loadStatus]);

    // F9.7 WAVE 2H · Duolingo-style confetti burst + toast + +100 credits
    // the first time an auth'd user lands with all 3 steps completed. The
    // claim endpoint is idempotent (DB flag `onboarding_v3.bonus_granted`)
    // so refreshes won't double-fire. We update local state eagerly to
    // prevent duplicate claims from React StrictMode double-invocations.
    const allStepsDone = Boolean(
        onboarding.steps?.email && onboarding.steps?.target && onboarding.steps?.campaign,
    );
    useEffect(() => {
        if (statusLoading) return;
        if (!allStepsDone) return;
        if (onboarding.bonus_granted) return;
        if (onboarding.skipped) return;
        let cancelled = false;

        (async () => {
            try {
                const res = await apiFetch("/dashboard-v3/onboarding/claim-bonus", { method: "POST" });
                if (cancelled) return;
                setOnboarding((prev) => ({ ...prev, bonus_granted: true, completed: true }));
                if (res?.already_granted) return;

                // Duolingo-style burst: center pop + side bursts for depth
                confetti({
                    particleCount: 160,
                    spread: 100,
                    origin: { y: 0.6 },
                    colors: ["#FFFFFF", "#CCCCCC", "#CCCCCC", "#FFFFFF"],
                });
                setTimeout(() => {
                    confetti({ particleCount: 90, spread: 70, origin: { x: 0.2, y: 0.55 }, colors: ["#FFFFFF", "#CCCCCC"] });
                    confetti({ particleCount: 90, spread: 70, origin: { x: 0.8, y: 0.55 }, colors: ["#CCCCCC", "#FFFFFF"] });
                }, 300);

                toast.success(t("onboarding.bonus_toast_title"), {
                    description: t("onboarding.bonus_toast_body", { amount: res?.bonus_credits ?? 100 }),
                    duration: 5000,
                });

                // Refresh AuthContext user so the header credits pill picks
                // up the new balance without requiring a page reload.
                try { if (refreshAuth) await refreshAuth(); } catch (_e) {}
            } catch (_e) {
                // Non-blocking: user still sees the dashboard. Next refresh retries.
            }
        })();

        return () => { cancelled = true; };
    }, [statusLoading, allStepsDone, onboarding.bonus_granted, onboarding.skipped, t, refreshAuth]);

    const bannerMode =
        !statusLoading && !(onboarding.skipped || onboarding.completed)
            ? "new"
            : "returning";

    const handleSkip = async () => {
        setSkipping(true);
        try {
            await apiFetch("/dashboard-v3/onboarding/skip", { method: "POST" });
            setOnboarding((prev) => ({ ...prev, skipped: true }));
        } catch (e) {
            toast.error(e.message || "Impossibile saltare l'onboarding.");
        } finally {
            setSkipping(false);
        }
    };

    if (!user) return null;

    return (
        <div
            data-testid="dashboard-v3-root"
            className="min-h-screen text-text-main"
            style={{ backgroundColor: "#0A0A0A" }}
        >
            {headerV3Enabled ? <HeaderV3 /> : <HeaderV2 />}

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
                <WelcomeBannerV3
                    bannerMode={bannerMode}
                    firstName={firstNameOf(user)}
                    steps={onboarding.steps}
                    onSkip={handleSkip}
                    skipping={skipping}
                />

                <div className="mt-8">
                    <StatsRowV3
                        credits={user.credits ?? 0}
                        leadsCount={MOCK_LEADS_COUNT}
                        campaignsCount={MOCK_CAMPAIGNS_COUNT}
                        reputationScore={user.reputation ?? MOCK_REPUTATION_SCORE}
                        onOpenTopUp={() => setTopUpOpen(true)}
                    />
                </div>

                {/* F11 Phase A · Current plan + usage widget */}
                <div className="mt-6">
                    <DashboardPlanWidget />
                </div>

                <QuickActionsRowV3 />
                <AIAgentsPreviewV3 />
                <RecentActivityV3 />

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="h-12"
                    aria-hidden
                />
            </main>

            <TopUpModalV3 open={topUpOpen} onClose={() => setTopUpOpen(false)} />
        </div>
    );
}
